import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Sun, Moon, X } from 'lucide-react';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  format,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO
} from 'date-fns';

interface JournalEntry {
  id: string;
  promptType: 'morning' | 'evening';
  completedAt: Date;
  reflectionDetails?: Array<{
    question: string;
    answer: string;
  }>;
}

interface JournalCalendarProps {
  entries: JournalEntry[];
  onDateSelect: (date: Date, entries: JournalEntry[]) => void;
}

export const JournalCalendar: React.FC<JournalCalendarProps> = ({
  entries,
  onDateSelect
}) => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1));
  };

  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1));
  };

  const getEntriesForDate = (date: Date): JournalEntry[] => {
    return entries.filter(entry => 
      isSameDay(entry.completedAt, date)
    );
  };

  const hasEntries = (date: Date): boolean => {
    return getEntriesForDate(date).length > 0;
  };

  const hasMorning = (date: Date): boolean => {
    return getEntriesForDate(date).some(e => e.promptType === 'morning');
  };

  const hasEvening = (date: Date): boolean => {
    return getEntriesForDate(date).some(e => e.promptType === 'evening');
  };

  const handleDateClick = (date: Date) => {
    const dateEntries = getEntriesForDate(date);
    if (dateEntries.length > 0) {
      setSelectedDate(date);
      onDateSelect(date, dateEntries);
    }
  };

  const renderHeader = () => {
    return (
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={prevMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        
        <h2 className="text-lg font-bold text-gray-900">
          {format(currentMonth, 'MMMM yyyy')}
        </h2>
        
        <button
          onClick={nextMonth}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5 text-gray-600" />
        </button>
      </div>
    );
  };

  const renderDays = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    
    return (
      <div className="grid grid-cols-7 gap-1 mb-2">
        {days.map(day => (
          <div
            key={day}
            className="text-center text-xs font-semibold text-gray-500 py-2"
          >
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const rows: JSX.Element[] = [];
    let days: JSX.Element[] = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day;
        const dayHasEntries = hasEntries(day);
        const dayHasMorning = hasMorning(day);
        const dayHasEvening = hasEvening(day);
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isDayToday = isToday(day);
        const isSelected = selectedDate && isSameDay(day, selectedDate);

        days.push(
          <button
            key={day.toString()}
            onClick={() => handleDateClick(cloneDay)}
            disabled={!dayHasEntries}
            className={`
              relative aspect-square p-1 rounded-lg transition-all
              ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-900'}
              ${isDayToday ? 'ring-2 ring-blue-500' : ''}
              ${isSelected ? 'bg-blue-100 ring-2 ring-blue-500' : ''}
              ${dayHasEntries ? 'hover:bg-gray-100 cursor-pointer' : 'cursor-default'}
              ${!dayHasEntries && isCurrentMonth ? 'opacity-40' : ''}
            `}
          >
            <div className="text-sm font-medium mb-1">
              {format(day, 'd')}
            </div>
            
            {/* Entry Indicators */}
            {dayHasEntries && (
              <div className="flex items-center justify-center gap-0.5 absolute bottom-1 left-1/2 -translate-x-1/2">
                {dayHasMorning && (
                  <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" title="Morning reflection" />
                )}
                {dayHasEvening && (
                  <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" title="Evening reflection" />
                )}
              </div>
            )}
          </button>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7 gap-1" key={day.toString()}>
          {days}
        </div>
      );
      days = [];
    }

    return <div className="space-y-1">{rows}</div>;
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
      {renderHeader()}
      {renderDays()}
      {renderCells()}
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-6 pt-4 border-t border-gray-200">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-2 h-2 rounded-full bg-yellow-500" />
          <span>Morning</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <div className="w-2 h-2 rounded-full bg-indigo-500" />
          <span>Evening</span>
        </div>
      </div>
    </div>
  );
};

interface EntryDetailModalProps {
  date: Date;
  entries: JournalEntry[];
  onClose: () => void;
}

export const EntryDetailModal: React.FC<EntryDetailModalProps> = ({
  date,
  entries,
  onClose
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h3 className="text-xl font-bold text-gray-900">
              {format(date, 'EEEE, MMMM d, yyyy')}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {entries.length} reflection{entries.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Entries */}
        <div className="p-6 space-y-6">
          {entries.map((entry, index) => (
            <div
              key={entry.id}
              className={`border-l-4 pl-4 ${
                entry.promptType === 'morning'
                  ? 'border-yellow-500'
                  : 'border-indigo-500'
              }`}
            >
              {/* Entry Header */}
              <div className="flex items-center gap-2 mb-3">
                {entry.promptType === 'morning' ? (
                  <Sun className="w-5 h-5 text-yellow-500" />
                ) : (
                  <Moon className="w-5 h-5 text-indigo-500" />
                )}
                <h4 className="font-semibold text-gray-900">
                  {entry.promptType === 'morning' ? 'Morning' : 'Evening'} Reflection
                </h4>
                <span className="text-sm text-gray-500">
                  {format(entry.completedAt, 'h:mm a')}
                </span>
              </div>

              {/* Reflection Details */}
              {entry.reflectionDetails && entry.reflectionDetails.length > 0 && (
                <div className="space-y-3">
                  {entry.reflectionDetails.map((detail, idx) => (
                    <div key={idx} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {detail.question}
                      </p>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">
                        {detail.answer}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};




